import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import classes from './date-header.module.css';
import { WEEK_WIDTH, getWeekEnd } from './utils';

interface DateHeaderProps {
  weekHeaders: Date[];
  totalWeeks: number;
}

export function DateHeader({ weekHeaders, totalWeeks }: DateHeaderProps) {
  // const { i18n } = useTranslation();
  // console.log(dayjs.locale(i18n.language));
  // Calculate month headers based on actual day positions, not week boundaries
  const monthHeaders: {
    month: number;
    year: number;
    leftOffset: number;
    width: number;
  }[] = [];

  if (weekHeaders.length === 0) {
    return null;
  }

  // Get the actual start date (first Monday) and end date (last Sunday)
  const timelineStart = dayjs(weekHeaders[0]); // First Monday
  const timelineEnd = dayjs(weekHeaders[weekHeaders.length - 1]).endOf(
    'isoWeek',
  ); // Last Sunday

  // Find all months that appear in the timeline
  const months: { month: number; year: number; firstDay: dayjs.Dayjs }[] = [];
  let currentMonth = timelineStart.startOf('month');

  while (
    currentMonth.isBefore(timelineEnd) ||
    currentMonth.isSame(timelineEnd, 'month')
  ) {
    // Only include months that actually intersect with our timeline
    const monthStart = currentMonth.startOf('month');
    const monthEnd = currentMonth.endOf('month');

    if (monthEnd.isAfter(timelineStart) && monthStart.isBefore(timelineEnd)) {
      months.push({
        month: currentMonth.month(),
        year: currentMonth.year(),
        firstDay: monthStart.isAfter(timelineStart)
          ? monthStart
          : timelineStart,
      });
    }

    currentMonth = currentMonth.add(1, 'month');
  }

  // Calculate position and width for each month
  months.forEach((monthInfo, index) => {
    const monthStart = dayjs()
      .year(monthInfo.year)
      .month(monthInfo.month)
      .startOf('month');
    const monthEnd = dayjs()
      .year(monthInfo.year)
      .month(monthInfo.month)
      .endOf('month');

    // Calculate the actual start position within the timeline
    const actualStart = monthStart.isAfter(timelineStart)
      ? monthStart
      : timelineStart;
    const actualEnd = monthEnd.isBefore(timelineEnd) ? monthEnd : timelineEnd;

    // Calculate offset from timeline start in days, then convert to pixels
    const offsetDays = actualStart.diff(timelineStart, 'day');
    const leftOffset = (offsetDays / 7) * WEEK_WIDTH;

    // Calculate width in days, then convert to pixels
    const widthDays = actualEnd.diff(actualStart, 'day') + 1; // +1 to include the end day
    const width = (widthDays / 7) * WEEK_WIDTH;

    monthHeaders.push({
      month: monthInfo.month,
      year: monthInfo.year,
      leftOffset,
      width,
    });
  });

  return (
    <div
      className={classes.container}
      style={{ minWidth: `${totalWeeks * WEEK_WIDTH}px` }}
    >
      {/* Month headers */}
      <div className={classes.monthsRow}>
        {monthHeaders.map((header) => (
          <div
            className={classes.monthHeader}
            key={`month-${header.month}-${header.year}`}
            style={{
              position: 'absolute',
              left: `${header.leftOffset}px`,
              width: `${header.width}px`,
            }}
          >
            {dayjs().month(header.month).year(header.year).format('MMMM YYYY')}
          </div>
        ))}
      </div>

      {/* Week headers */}
      <div className={classes.daysRow}>
        {weekHeaders.map((weekStart, index) => {
          const weekEnd = getWeekEnd(weekStart);

          // Check if this is the first week of a new month
          // Use Wednesday of the week to determine the primary month for consistency
          const currentMidWeek = dayjs(weekStart).add(2, 'day'); // Wednesday
          const weekMonth = currentMidWeek.month();

          let previousMonth = -1;
          if (index > 0) {
            const previousMidWeek = dayjs(weekHeaders[index - 1]).add(2, 'day'); // Previous week's Wednesday
            previousMonth = previousMidWeek.month();
          }

          const isNewMonth = index === 0 || weekMonth !== previousMonth;

          const isEven = index % 2 === 0;

          const weekHeaderClasses = [
            classes.dayHeader,
            isNewMonth && classes.dayHeaderFirstOfMonth,
            classes.dayHeaderRegular,
            isEven ? classes.dayHeaderEven : classes.dayHeaderOdd,
          ]
            .filter(Boolean)
            .join(' ');

          const weekNumberClasses = [
            classes.dayNumber,
            isNewMonth && classes.dayNumberFirstOfMonth,
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <div
              className={weekHeaderClasses}
              key={index}
              style={{ width: `${WEEK_WIDTH}px` }}
            >
              <span className={weekNumberClasses}>
                {dayjs(weekStart).format('MMM DD')} -{' '}
                {dayjs(weekEnd).format('DD')}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
